import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
  Row,
  Col,
  Alert
} from "reactstrap";
import axios from "axios";
import { API_BASE_URL } from "../../../constants";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { Toast } from "primereact/toast";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const StaffBlogAdd = () => {
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const navigate = useNavigate();
  const toast = useRef(null);
  const editorContainerRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [loadingBlog, setLoadingBlog] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [content, setContent] = useState('');
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    content: "",
    image: null,
    status: "draft",
    meta_title: "",
    meta_description: "",
    meta_keywords: "",
    tags: ""
  });
  const [imagePreview, setImagePreview] = useState(null);

  const breadcrumbItems = [
    { title: "Content", link: "/" },
    { title: "Blogs", link: "/admin/office/website/blogs" },
    { title: editId ? "Edit Blog" : "Add Blog", link: "#!" },
  ];

  const statusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'published', label: 'Published' },
    // { value: 'archived', label: 'Archived' },
    // { value: 'pending_review', label: 'Pending Review' }
  ];

  // Quill modules and formats configuration
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['link', 'image', 'video'],
      [{ 'color': [] }, { 'background': [] }],
      ['clean']
    ],
    clipboard: {
      matchVisual: false,
    }
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'indent',
    'align',
    'link', 'image', 'video',
    'color', 'background'
  ];

  // This function fetches blog data by first getting the list and then finding the specific blog
  const fetchBlogDataFromList = React.useCallback(async () => {
    setLoadingBlog(true);
    setFetchError(null);

    try {
      // Get the full list of blogs first
      const listUrl = `${API_BASE_URL}/blog/list`;
      console.log(`Fetching blog list from: ${listUrl}`);

      const response = await axios.get(listUrl, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") }
      });

      if (!response.data || !response.data.blogs || !Array.isArray(response.data.blogs)) {
        throw new Error("Invalid blog list data structure");
      }

      // Find the specific blog by ID
      // eslint-disable-next-line eqeqeq
      const blogData = response.data.blogs.find(blog => blog.id == editId);

      if (!blogData) {
        throw new Error(`Blog with ID ${editId} not found in the list`);
      }

      console.log("Blog data found:", blogData);

      setFormData({
        title: blogData.title || "",
        description: blogData.description || "",
        content: blogData.content || "",
        image: null,
        status: blogData.status || "draft",
        meta_title: blogData.meta_title || "",
        meta_description: blogData.meta_description || "",
        meta_keywords: blogData.meta_keywords || "",
        tags: blogData.tags || ""
      });

      // Set the content directly for Quill editor
      setContent(blogData.content || '');

      if (blogData.image_url) {
        setImagePreview(blogData.image_url);
      }
    } catch (error) {
      console.error("Error loading blog data:", error);

      const errorMessage = error.message || "Failed to load blog data";

      setFetchError(errorMessage);
      showToast('error', 'Error', errorMessage);
    } finally {
      setLoadingBlog(false);
    }
  }, [editId]);

  useEffect(() => {
    if (editId) {
      fetchBlogDataFromList();
    }
  }, [editId, fetchBlogDataFromList]);

  // Update formData content when Quill content changes
  useEffect(() => {
    setFormData(prevState => ({
      ...prevState,
      content: content
    }));
  }, [content]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleContentChange = (value) => {
    setContent(value);
  };

  const showToast = (severity, summary, detail) => {
    toast.current?.show({ severity, summary, detail, life: 3000 });
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      showToast('error', 'Error', 'Blog title is required');
      return false;
    }

    const strippedContent = content.replace(/<(.|\n)*?>/g, '').trim();
    if (!strippedContent) {
      showToast('error', 'Error', 'Blog content is required');
      return false;
    }

    if (!formData.meta_keywords.trim()) {
      showToast('error', 'Error', 'Meta keywords are required');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();

      // Append all form fields except image
      Object.keys(formData).forEach(key => {
        if (key !== 'image') {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Append image only if it exists
      if (formData.image) {
        formDataToSend.append('image', formData.image);
      }

      const url = editId
        ? `${API_BASE_URL}/blog/update/${editId}`
        : `${API_BASE_URL}/blog/add`;

      console.log(`Submitting form to: ${url}`);

      const response = await axios({
        method: 'post',
        url: url,
        data: formDataToSend,
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
          "Content-Type": "multipart/form-data",
        }
      });

      console.log("API response:", response.data);
      showToast('success', 'Success', editId ? 'Blog updated successfully' : 'Blog created successfully');

      // Navigate after a brief delay to allow the toast to be seen
      setTimeout(() => {
        navigate('/admin/office/website/blogs');
      }, 1500);
    } catch (error) {
      console.error("Error saving blog:", error);

      const errorMsg = error.response?.data?.message ||
        (editId ? 'Failed to update blog' : 'Failed to create blog');

      showToast('error', 'Error', errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/office/website/blogs');
  };

  if (loadingBlog) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Edit Blog" breadcrumbItems={breadcrumbItems} />
          <Card>
            <CardBody className="text-center py-5">
              <Spinner color="primary" />
              <p className="mt-2">Loading blog data...</p>
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={editId ? "Edit Blog" : "Add Blog"} breadcrumbItems={breadcrumbItems} />
          <Card>
            <CardHeader>
              <h4 className="card-title mb-0">{editId ? 'Edit Blog' : 'Create New Blog'}</h4>
            </CardHeader>
            <CardBody>
              {fetchError && (
                <Alert color="danger" className="mb-4">
                  {fetchError}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col lg={8}>
                    <FormGroup>
                      <Label for="title">Title <span className="text-danger">*</span></Label>
                      <Input
                        type="text"
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder="Enter blog title"
                        required
                      />
                    </FormGroup>

                    {/* <FormGroup>
                      <Label for="description">Short Description</Label>
                      <Input
                        type="textarea"
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="Enter a short description"
                        rows={3}
                      />
                    </FormGroup> */}

                    <FormGroup>
                      <Label for="content">Content <span className="text-danger">*</span></Label>
                      <div ref={editorContainerRef} className="border rounded">
                        <ReactQuill
                          theme="snow"
                          value={content}
                          onChange={handleContentChange}
                          modules={modules}
                          formats={formats}
                          placeholder="Write your blog content here..."
                          style={{ height: '300px', marginBottom: '50px' }}
                        />
                      </div>
                    </FormGroup>
                  </Col>

                  <Col lg={4}>
                    <Card className="shadow-sm">
                      <CardBody>
                        <FormGroup>
                          <Label for="sts">Status</Label>
                          <Input
                            type="select"
                            id="sts"
                            name="status"
                            value={formData.status}
                            onChange={handleInputChange}
                            className="form-select"
                          >
                            {statusOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Input>
                        </FormGroup>

                        <FormGroup>
                          <Label for="image">Featured Image</Label>
                          <Input
                            type="file"
                            id="image"
                            name="image"
                            onChange={handleImageChange}
                            accept="image/*"
                          />
                          {imagePreview && (
                            <div className="mt-2">
                              <img
                                src={imagePreview}
                                alt="Preview"
                                className="img-thumbnail"
                                style={{ maxHeight: "150px" }}
                              />
                            </div>
                          )}
                        </FormGroup>

                        {/* <FormGroup>
                          <Label for="tags">Tags</Label>
                          <Input
                            type="text"
                            id="tags"
                            name="tags"
                            value={formData.tags}
                            onChange={handleInputChange}
                            placeholder="Enter tags separated by commas"
                          />
                        </FormGroup> */}
                      </CardBody>
                    </Card>

                    <Card className="mt-3 shadow-sm">
                      <CardHeader>
                        <h5 className="mb-0">SEO Settings</h5>
                      </CardHeader>
                      <CardBody>
                        <FormGroup>
                          <Label for="meta_title">Meta Title</Label>
                          <Input
                            type="text"
                            id="meta_title"
                            name="meta_title"
                            value={formData.meta_title}
                            onChange={handleInputChange}
                            placeholder="Enter meta title"
                          />
                        </FormGroup>

                        <FormGroup>
                          <Label for="meta_description">Meta Description</Label>
                          <Input
                            type="textarea"
                            id="meta_description"
                            name="meta_description"
                            value={formData.meta_description}
                            onChange={handleInputChange}
                            rows={3}
                            placeholder="Enter meta description"
                          />
                        </FormGroup>

                        <FormGroup>
                          <Label for="meta_keywords">Meta Keywords <span className="text-danger">*</span></Label>
                          <Input
                            type="text"
                            id="meta_keywords"
                            name="meta_keywords"
                            value={formData.meta_keywords}
                            onChange={handleInputChange}
                            placeholder="Enter meta keywords separated by commas"
                            required
                          />
                        </FormGroup>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                <div className="mt-4 d-flex justify-content-between">
                  <Button
                    type="button"
                    color="light"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" color="primary" disabled={loading}>
                    {loading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        {editId ? 'Updating...' : 'Saving...'}
                      </>
                    ) : (
                      editId ? 'Update Blog' : 'Save Blog'
                    )}
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>
        </Container>
      </div>
      <Toast ref={toast} position="top-right" />
    </React.Fragment>
  );
};

export default StaffBlogAdd;